const API_BASE = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  ? 'https://tkxel-seo-worker.laptoper-com.workers.dev/api'
  : '/api';

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Cross-site cookies (pages.dev frontend + workers.dev API) get blocked by some
// browsers' third-party cookie policies, so auth also travels via this token.
const AUTH_TOKEN_KEY = 'auth_token';
export const getAuthToken = () => localStorage.getItem(AUTH_TOKEN_KEY);
export const setAuthToken = (token: string) => localStorage.setItem(AUTH_TOKEN_KEY, token);
export const clearAuthToken = () => localStorage.removeItem(AUTH_TOKEN_KEY);

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
    ...options,
  });

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const data = await res.json() as { error?: string };
      message = data.error || message;
    } catch {}
    throw new ApiError(res.status, message);
  }

  // Handle CSV responses
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('text/csv')) {
    return res.text() as unknown as T;
  }

  return res.json() as Promise<T>;
}

export interface AuthUser {
  id: string;
  username: string;
  role: string;
  team_member_name: string | null;
  display_name: string | null;
}

// Auth
export const authApi = {
  login: async (username: string, password: string) => {
    const data = await request<{ success: boolean; token: string; user: AuthUser }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    setAuthToken(data.token);
    return data;
  },
  logout: async () => {
    try {
      return await request<{ success: boolean }>('/auth/logout', { method: 'POST' });
    } finally {
      clearAuthToken();
    }
  },
  me: () => request<{ user: AuthUser }>('/auth/me'),
};

// Dashboard
const qs = (params?: Record<string, string>) => {
  const query = params ? new URLSearchParams(params).toString() : '';
  return query ? `?${query}` : '';
};

export const dashboardApi = {
  stats: (params?: Record<string, string>) => request<any>(`/dashboard/stats${qs(params)}`),
  progressByPageType: (params?: Record<string, string>) => request<any>(`/dashboard/progress-by-page-type${qs(params)}`),
  teamWorkload: (params?: Record<string, string>) => request<any>(`/dashboard/team-workload${qs(params)}`),
  blockedTasks: (params?: Record<string, string>) => request<any>(`/dashboard/blocked-tasks${qs(params)}`),
  pendingTasks: (params?: Record<string, string>) => request<any>(`/dashboard/pending-tasks${qs(params)}`),
  completedTasks: (params?: Record<string, string>) => request<any>(`/dashboard/completed-tasks${qs(params)}`),
  leadStats: () => request<any>('/dashboard/lead-stats'),
  leadDetail: (id: number) => request<any>(`/dashboard/leads/${id}`),
  leadsList: (period: string) => request<any>(`/dashboard/leads?period=${period}`),
};

// AI helpers
export const aiApi = {
  improveNotes: (text: string) =>
    request<{ improved: string; model: string }>('/ai/improve', { method: 'POST', body: JSON.stringify({ text }) }),
};

// Google / Gmail leads
export const googleApi = {
  status: () => request<any>('/google/status'),
  connectUrl: () => `${API_BASE}/google/connect`,
  sync: () => request<any>('/google/sync', { method: 'POST' }),
  disconnect: () => request<any>('/google/disconnect', { method: 'POST' }),
};

// Sitewide Tasks
export const sitewideApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/sitewide-tasks${qs}`);
  },
  create: (data: any) =>
    request<any>('/sitewide-tasks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request<any>(`/sitewide-tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request<any>(`/sitewide-tasks/${id}`, { method: 'DELETE' }),
  bulkDelete: (ids: number[]) =>
    request<any>('/sitewide-tasks/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
};

// Page Tasks
export const pageTasksApi = {
  list: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<any>(`/page-tasks${qs}`);
  },
  create: (data: any) =>
    request<any>('/page-tasks', { method: 'POST', body: JSON.stringify(data) }),
  createBatch: (data: any) =>
    request<any>('/page-tasks/batch', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    request<any>(`/page-tasks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) =>
    request<any>(`/page-tasks/${id}`, { method: 'DELETE' }),
  bulkDelete: (ids: number[]) =>
    request<any>('/page-tasks/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) }),
};

// Page Types
export const pageTypesApi = {
  list: () => request<any>('/page-types'),
};

// Pages (sitemap URLs)
export const pagesApi = {
  list: (page_type_id: number, search?: string, website_id?: number | string | null) => {
    const params = new URLSearchParams({ page_type_id: String(page_type_id) });
    if (search) params.set('search', search);
    if (website_id) params.set('website_id', String(website_id));
    return request<any>(`/pages?${params}`);
  },
};

// Weekly Plan
export const weeklyPlanApi = {
  list: (params?: Record<string, string | undefined>) => {
    const filtered = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v)) as Record<string, string>;
    const qs = Object.keys(filtered).length ? '?' + new URLSearchParams(filtered).toString() : '';
    return request<any>(`/weekly-plan${qs}`);
  },
  updateStatus: (source: string, id: number, status: string) =>
    request<any>(`/weekly-plan/${source}/${id}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    }),
};

// Trash / deleted tasks
export const trashApi = {
  tasks: (params?: Record<string, string>) => request<any>(`/trash/tasks${qs(params)}`),
  restore: (source: string, id: number) =>
    request<any>(`/trash/${source}/${id}/restore`, { method: 'POST' }),
};

// Export
export const exportApi = {
  csv: (params?: Record<string, string>) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request<string>(`/export/csv${qs}`);
  },
  logs: () => request<any>('/export/logs'),
};

// Settings
export const settingsApi = {
  teamMembers: {
    list: () => request<any>('/settings/team-members'),
    create: (data: { name: string; team: string }) =>
      request<any>('/settings/team-members', { method: 'POST', body: JSON.stringify(data) }),
    delete: (id: number) =>
      request<any>(`/settings/team-members/${id}`, { method: 'DELETE' }),
  },
  websites: {
    list: () => request<any>('/settings/websites'),
    create: (data: { name: string; url?: string | null }) =>
      request<any>('/settings/websites', { method: 'POST', body: JSON.stringify(data) }),
    update: (id: number, data: { name?: string; url?: string | null }) =>
      request<any>(`/settings/websites/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    delete: (id: number) =>
      request<any>(`/settings/websites/${id}`, { method: 'DELETE' }),
  },
  pageTypes: {
    update: (id: number, data: any) =>
      request<any>(`/settings/page-types/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  },
  password: (data: { current_password: string; new_password: string }) =>
    request<any>('/settings/password', { method: 'PUT', body: JSON.stringify(data) }),
  general: {
    get: () => request<any>('/settings/general'),
    update: (data: Record<string, string>) =>
      request<any>('/settings/general', { method: 'PUT', body: JSON.stringify(data) }),
  },
  cleanup: () => request<any>('/settings/cleanup', { method: 'POST' }),
};

// User management (admin only)
export const usersApi = {
  list: () => request<any>('/settings/users'),
  create: (data: any) => request<any>('/settings/users', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) => request<any>(`/settings/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: number) => request<any>(`/settings/users/${id}`, { method: 'DELETE' }),
};

export { ApiError };
